import React, { useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, ScaleIcon } from 'lucide-react';
interface TKLineSelectionProps {
  onBackToOverview?: () => void;
  onLineSelect: (line: string, weighingMode: 'line' | 'output') => void;
}
export function TKLineSelection({
  onBackToOverview,
  onLineSelect
}: TKLineSelectionProps) {
  // State für den Wiegemodus
  const [weighingMode, setWeighingMode] = useState<'line' | 'output'>('line');
  // Toggle-Funktion für den Wiegemodus
  const toggleWeighingMode = () => {
    setWeighingMode(weighingMode === 'line' ? 'output' : 'line');
  };
  const tkLines = [{
    id: 'tk1',
    name: 'TK Linie 1',
    description: 'Hauptlinie für Paella und Risotto',
    status: 'active'
  }, {
    id: 'tk2',
    name: 'TK Linie 2',
    description: 'Nudelgerichte und Saucen',
    status: 'active'
  }, {
    id: 'tk3',
    name: 'TK Linie 3',
    description: 'Gemüsemischungen und Beilagen',
    status: 'maintenance'
  }, {
    id: 'tk4',
    name: 'TK Linie 4',
    description: 'Fleischprodukte und Fertiggerichte',
    status: 'active'
  }];
  return <div className="container mx-auto" data-id="element-2284">
      <div className="mb-8" data-id="element-2285">
        <div data-id="element-2286">
          <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-2287">TK Linien</h2>
          <p className="text-gray-600 mb-4" data-id="element-2288">
            Wählen Sie die TK Linie aus, an der Sie arbeiten
          </p>
          {onBackToOverview}
        </div>
      </div>
      {/* Waagen-Umschalter */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6" data-id="element-2289">
        <div className="flex items-center justify-between" data-id="element-2290">
          <div className="flex items-center" data-id="element-2291">
            <ScaleIcon size={20} className="text-gray-700 mr-2" data-id="element-2292" />
            <h3 className="text-lg font-semibold text-gray-800" data-id="element-2293">
              Waagen-Auswahl
            </h3>
          </div>
          <div className="flex items-center" data-id="element-2294">
            <span className={`mr-3 ${weighingMode === 'line' ? 'font-medium text-blue-600' : 'text-gray-500'}`} data-id="element-2295">
              Linienwaage
            </span>
            <div className="relative inline-flex cursor-pointer" onClick={toggleWeighingMode} data-id="element-2296">
              <div className={`
                w-12 h-6 rounded-full transition-colors duration-200 ease-in-out
                ${weighingMode === 'line' ? 'bg-blue-500' : 'bg-green-500'}
              `} data-id="element-2297">
                <div className={`
                  absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow transform transition-transform duration-200 ease-in-out
                  ${weighingMode === 'output' ? 'translate-x-6' : ''}
                `} data-id="element-2298"></div>
              </div>
            </div>
            <span className={`ml-3 ${weighingMode === 'output' ? 'font-medium text-green-600' : 'text-gray-500'}`} data-id="element-2299">
              Ausgangswaage
            </span>
          </div>
        </div>
        <div className="mt-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg" data-id="element-2300">
          {weighingMode === 'line' ? <p data-id="element-2301">
              <span className="font-medium" data-id="element-2302">Linienwaage:</span> Die Verwiegung
              erfolgt direkt an der Linie. Jede Box wird einzeln gewogen und
              verarbeitet.
            </p> : <p data-id="element-2303">
              <span className="font-medium" data-id="element-2304">Ausgangswaage:</span> Die Verwiegung
              erfolgt am Ausgang. Materialien werden gesammelt und als Lot
              verwogen.
            </p>}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2305">
        {tkLines.map(line => <div key={line.id} onClick={() => line.status !== 'maintenance' && onLineSelect(line.id, weighingMode)} className={`
              relative overflow-hidden bg-white rounded-xl shadow-lg p-0
              transition-all duration-300 group
              ${line.status === 'maintenance' ? 'border border-amber-300 cursor-default' : 'border border-gray-100 cursor-pointer hover:shadow-xl hover:translate-y-[-4px]'}
            `} data-id="element-2306">
            {/* Status indicator */}
            <div className="absolute top-4 right-4 z-10" data-id="element-2307">
              {line.status === 'maintenance' ? <div className="flex items-center px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium border border-amber-200" data-id="element-2308">
                  <span className="w-2 h-2 bg-amber-500 rounded-full mr-2 animate-pulse" data-id="element-2309"></span>
                  Wartung
                </div> : <div className="flex items-center px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium border border-green-200" data-id="element-2310">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2" data-id="element-2311"></span>
                  Aktiv
                </div>}
            </div>
            {/* Card content */}
            <div className="p-8" data-id="element-2312">
              <h3 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-2313">
                {line.name}
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-2314">{line.description}</p>
              <div className={`
                flex justify-between items-center mt-auto pt-4 border-t
                ${line.status === 'maintenance' ? 'border-amber-100' : 'border-gray-100'}
              `} data-id="element-2315">
                <div className={`
                  text-sm font-medium
                  ${line.status === 'maintenance' ? 'text-amber-600' : 'text-green-600'}
                `} data-id="element-2316">
                  {line.status === 'maintenance' ? 'Derzeit in Wartung' : 'Bereit für Produktion'}
                </div>
                <button className={`
                    flex items-center gap-2 px-5 py-2.5 rounded-lg text-white transition-colors
                    ${line.status === 'maintenance' ? 'bg-gray-400 cursor-not-allowed opacity-60' : 'bg-[#8DC63F] hover:bg-[#7AB929] group-hover:shadow-md'}
                  `} disabled={line.status === 'maintenance'} onClick={e => {
              e.stopPropagation();
              if (line.status !== 'maintenance') {
                onLineSelect(line.id, weighingMode);
              }
            }} data-id="element-2317">
                  <span data-id="element-2318">Auswählen</span>
                  <ArrowRightIcon size={18} className={line.status !== 'maintenance' ? 'group-hover:translate-x-1 transition-transform' : ''} data-id="element-2319" />
                </button>
              </div>
            </div>
            {/* Background decoration for active lines */}
            {line.status !== 'maintenance' && <div className="absolute inset-0 bg-gradient-to-br from-[#8DC63F]/5 to-transparent pointer-events-none" data-id="element-2320"></div>}
          </div>)}
      </div>
    </div>;
}